import { useTranslation } from 'react-i18next';

import { formatAgentStateName } from './format-state-name';
import s from './styles.module.scss';

import type { FC } from 'react';

/**
 * Amazon agent error-state name → the sentence an agent can act on. These are
 * the AgentErrorStates a failed outbound call parks the agent in; connectService
 * captures the name when the contact ENDS without ever connecting.
 */
const REASON_KEYS = {
  LineEngagedCustomer: 'connectPanel.failure.reason.lineEngagedCustomer',
  MissedCallCustomer: 'connectPanel.failure.reason.missedCallCustomer',
  FailedConnectCustomer: 'connectPanel.failure.reason.failedConnectCustomer',
  BadAddressCustomer: 'connectPanel.failure.reason.badAddressCustomer',
  LineEngagedAgent: 'connectPanel.failure.reason.lineEngagedAgent',
  MissedCallAgent: 'connectPanel.failure.reason.missedCallAgent',
  FailedConnectAgent: 'connectPanel.failure.reason.failedConnectAgent',
  BadAddressAgent: 'connectPanel.failure.reason.badAddressAgent',
  AgentHungUp: 'connectPanel.failure.reason.agentHungUp',
  MultipleCcpWindows: 'connectPanel.failure.reason.multipleCcpWindows',
  RealtimeCommunicationError:
    'connectPanel.failure.reason.realtimeCommunicationError',
} as const;

/** The literal-key union keeps the dynamic lookup inside i18next's typed t(). */
type TReasonKey = (typeof REASON_KEYS)[keyof typeof REASON_KEYS];

// Error states that name no cause. Their humanised form — "Default", "Error" —
// would read as a broken UI, so they fall back to the generic line instead.
const UNINFORMATIVE = new Set(['Default', 'Error']);

interface IProps {
  /** Amazon agent error-state name, or null when Connect reported none. */
  failureStateName: string | null;
}

/**
 * Takes CallSummary's slot when the contact ENDED without ever connecting —
 * a wrap-up board with 00:00 of talk time explains nothing, and QA watched
 * agents meet exactly that. Same flex:1 geometry, so the panel holds 402px.
 */
const CallFailed: FC<IProps> = ({ failureStateName }) => {
  const { t } = useTranslation();

  const knownKey = failureStateName
    ? (REASON_KEYS as Record<string, TReasonKey>)[failureStateName]
    : undefined;
  const reason = (() => {
    if (knownKey) return t(knownKey);
    // An unmapped name is still a reason — "Realtime Communication Error"
    // beats saying nothing — so it is shown humanised rather than dropped.
    if (failureStateName && !UNINFORMATIVE.has(failureStateName)) {
      return formatAgentStateName(failureStateName);
    }
    return t('connectPanel.failure.generic');
  })();

  return (
    <div className={s.failed} data-cy="call-failed">
      <span className={s['failed-title']}>
        {t('connectPanel.failure.title')}
      </span>
      <span className={s['failed-reason']} data-cy="call-failed-reason">
        {reason}
      </span>
      <span className={s['failed-hint']}>
        {t('connectPanel.failure.tryAgain')}
      </span>
    </div>
  );
};

export default CallFailed;
